// Import
import firebase from '../config/FirebaseConfig'
import store from '../../store'
import EventBus from '../../plugins/BaseNotifications/EventBus'

// Setup
const db = firebase.firestore()

// MUST KEEP IN SYNC WITH FIRESTORE RULES ALLOWLIST
const ALLOWED_UPDATE_FIELDS = new Set([
  'adSettings',
  'avatar',
  'companyType',
  'company_email',
  'company_name',
  'company_phone_number',
  'company_website',
  'defaultLanguages',
  'email',
  'expertProfile',
  'favoritedBrands',
  'favoritedHooks',
  'first_name',
  'igBoardIds',
  'igConversationId',
  'igUsername',
  'igVerificationCode',
  'igWaitingVerification',
  'last_name',
  'lastSeen',
  'name',
  'onboardingComplete',
  'onboardingForm',
  'onboardingStatus',
  'phone_number',
  'recentBoards',
  'recentSearches',
  'starredBoards',
  'storageUsed',
  'trialStarted',
  'user_id',
  'viewedProductHuntModal'
])

// Strips any fields not in the allowlist before writing to Firestore
const sanitizePayload = (payload) => {
  const sanitized = {}
  for (const key of Object.keys(payload)) {
    if (ALLOWED_UPDATE_FIELDS.has(key)) {
      sanitized[key] = payload[key]
    }
  }
  return sanitized
}

const FORMER_TEAMMATE_PUBLIC_PROFILE = {
  name: 'Former teammate',
  avatar: null,
  first_name: null,
  last_name: null
}
const UNKNOWN_USER_PROFILE = {
  name: 'Unknown User',
  avatar: null,
  first_name: null,
  last_name: null,
  email: null
}

const getById = async (userId) => {
  let uid = userId; let dbCollection = 'auth-users'
  if (userId.split('-')[0] === 'legacy') { // In case the id references a legacy team member
    uid = userId.split('-')[1]
    dbCollection = 'legacy-team-members'
  }

  try {
    const user = await db.collection(dbCollection).doc(uid).get()

    if (!user?.exists) {
      return {
        id: userId,
        ...UNKNOWN_USER_PROFILE
      }
    }

    return {
      id: userId,
      ...user.data()
    }
  } catch (error) {
    if (error?.code === 'permission-denied') {
      return {
        id: userId,
        ...UNKNOWN_USER_PROFILE
      }
    }

    throw error
  }
}

const getPublicById = async (userId) => {
  let uid = userId; let dbCollection = 'auth-users'
  if (userId.split('-')[0] === 'legacy') { // In case the id references a legacy team member
    uid = userId.split('-')[1]
    dbCollection = 'legacy-team-members'
  }

  try {
    const user = await db.collection(dbCollection).doc(uid).get()
    const userData = user?.data?.()

    if (!user?.exists || !userData) {
      return {
        id: userId,
        ...FORMER_TEAMMATE_PUBLIC_PROFILE
      }
    }

    return {
      id: userId,
      name: userData.name,
      avatar: userData.avatar ?? null,
      first_name: userData.first_name ?? null,
      last_name: userData.last_name ?? null,
      email: userData.email ?? null
    }
  } catch (error) {
    if (error?.code === 'permission-denied') {
      return {
        id: userId,
        ...FORMER_TEAMMATE_PUBLIC_PROFILE
      }
    }

    throw error
  }
}

const getByEmail = async (email) => {
  const snapshot = await db.collection('auth-users').where('email', '==', email).get()
  if (!snapshot.docs.length) return undefined
  return {
    ...snapshot.docs[0].data(),
    id: snapshot.docs[0].id
  }
}

const checkUserSubscription = async (uid) => {
  if (uid.split('-')[0] === 'legacy') return false // legacy team members don't have subscriptions

  const db = firebase.firestore()

  const user = await db.collection('auth-users').doc(uid).get()

  let userHasSubscription = false

  if (user.exists) {
    const trialStartDate = user?.data()?.trialStarted

    const docs = await db
      .collection('customers')
      .doc(uid)
      .collection('subscriptions')
      .where('status', 'in', ['trialing', 'active'])
      .get()

    userHasSubscription = !!docs.docs.length || Date.now() < trialStartDate + 6.048e8
  }

  // check if the user is a part of a team
  if (!userHasSubscription) {
    const snapshot = await db
      .collection('teams')
      .where('members', 'array-contains', uid)
      .get()

    if (snapshot.docs?.length !== 0) {
      return true
    }
  }

  return userHasSubscription
}

const checkTeamSubscription = async (teamId) => {
  const team = await db.collection('teams').doc(teamId).get()

  // If the team was deleted, we check the subscription of the owner when it was deleted
  let teamCreatedBy = team.data().created_by
  if (teamCreatedBy.split('-')[0] === 'legacy') {
    const legacyId = teamCreatedBy.split('-')[1]
    const legacyMember = team.data()?.legacyMembers?.find(member => member.legacyUserId === legacyId)
    teamCreatedBy = legacyMember?.userId ?? teamCreatedBy
  }

  const user = await db
    .collection('auth-users')
    .doc(teamCreatedBy)
    .get()
  const trialStartDate = user.data().trialStarted

  const docs = await db
    .collection('customers')
    .doc(user.id)
    .collection('subscriptions')
    .where('status', 'in', ['trialing', 'active'])
    .get()

  return !!docs.docs.length || Date.now() < trialStartDate + 6.048e8
}

const getActiveSubscriptionInfo = async (userId) => {
  console.log('Fetching subscription info for user', userId)

  const subscriptionDoc = await db
    .collection('customers')
    .doc(userId)
    .collection('subscriptions')
    .where('status', '==', 'active')
    .get()

  return subscriptionDoc?.docs?.[0]?.data() ?? null
}

// Check if user exists and create if not (for new users)
const checkUserExists = async (user) => {
  const userFound = await db.collection('auth-users').doc(user.uid).get()

  if (userFound.data() && !userFound.data().trialStarted) {
    await db.collection('auth-users').doc(user.uid).update({
      user_id: user.uid,
      email: user.email,
      name: user.displayName,
      avatar: user.photoURL,
      trialStarted: Date.now()
    })
  }

  if (!userFound.data()) {
    console.log('User not found, create a new user!')

    const newUserPayload = {
      user_id: user.uid,
      email: user.email,
      name: user.displayName,
      avatar: user.photoURL,
      trialStarted: Date.now(),
      onboardingStatus: 'TEAM'
    }
    await db.collection('auth-users').doc(user.uid).set(newUserPayload)
    store.commit('AuthModule/SET_USER', newUserPayload)
  } else {
    store.commit('AuthModule/SET_USER', {
      ...userFound.data(),
      email: user.email,
      user_id: user.uid,
      token: user.uid
    })
  }

  if (userFound.exists) {
    const userData = userFound.data()
    const limits = {
      spyderLimit: userData.spyderLimit,
      lensLimit: userData.lensLimit,
      additionalLensLimit: userData.additionalLensLimit,
      lensTrialStarted: userData.lensTrialStarted
    }
    // Set app limits here
    return { appLimits: limits }
  }

  return {}
}

// Update User
const update = async (userId, payload) => {
  let uid = userId; let dbCollection = 'auth-users'
  if (userId.split('-')[0] === 'legacy') { // In case the id references a legacy team member
    uid = userId.split('-')[1]
    dbCollection = 'legacy-team-members'
  }
  const sanitized = sanitizePayload(payload)
  if (Object.keys(sanitized).length === 0) return
  try {
    return await db.collection(dbCollection).doc(uid).update(sanitized)
  } catch (error) {
    if (error?.code === 'permission-denied') {
      console.error('[UsersServices.update] Permission denied — attempted fields:', Object.keys(sanitized), error)
      EventBus.$emit('showAlert', {
        type: 'error',
        message: 'Permission denied: unable to proceed. Please contact support.'
      })
      return
    }
    throw error
  }
}

// Set last seen
const updateLastSeen = async () => {
  const user = firebase.auth().currentUser

  return await db
    .collection('auth-users')
    .doc(user.uid)
    .update({ lastSeen: +new Date() })
}

const updateExpert = async (user, updatePayload) => {
  if (!user) return
  const expertToUpdate = updatePayload.expert
  const avatarToUpdate = updatePayload.avatar

  const updateData = {
    expertProfile: expertToUpdate
  }
  if (avatarToUpdate) {
    updateData.avatar = avatarToUpdate
  }

  await db.collection('auth-users').doc(user?.user_id).update(updateData)
}

export default {
  checkUserExists,
  checkUserSubscription,
  getById,
  getPublicById,
  getByEmail,
  update,
  updateLastSeen,
  checkTeamSubscription,
  getActiveSubscriptionInfo,
  // Expert operations
  updateExpert
}
